import {Component, DestroyRef, inject, Input, OnDestroy, OnInit} from '@angular/core';
import {
  Adres,
  BokkzVpbDataService,
  CorporateTaxEntity,
  Deelneming,
  VpbAangifte
} from '@snelstart-ventures/fiscaal/domain-logic';
import {FormArray, FormControl, FormGroup} from '@angular/forms';
import {SelectFormControlItem, SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {debounceTime, filter, Observable, shareReplay, tap} from 'rxjs';
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {SharedUiDialogModule} from '@snelstart/ui-dialog';
import {SharedUiButtonModule} from '@snelstart/ui-button';
import {CommonModule} from '@angular/common';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';

@Component({
  selector: 'snelstart-venture-fiscaal-feature-deelnemingen',
  templateUrl: './deelnemingen.component.html',
  styleUrls: ['./deelnemingen.component.scss'],
  imports: [
    CommonModule,
    SharedUiDetailsModule,
    SharedUiFormControlsModule,
    SharedUiDialogModule,
    SharedUiButtonModule
  ]
})
export class DeelnemingenComponent implements OnInit, OnDestroy {

  private typeSafeFormShape!: {
    deelneming: FormGroup<{
      naam: FormControl<string | null>;
      fiscaalNummer: FormControl<string | null>;
      percentageAandelenbezit: FormControl<number | null>;
      nominaleWaardeAandelenbezit: FormControl<number | null>;
      opgeofferdBedrag: FormControl<number | null>;
      balanswaardering: FormControl<number | null>;
      voordelenBoekjaar: FormControl<number | null>;
      vorderingOpDeelneming: FormControl<number | null>;
      renteOntvangenVanDeelneming: FormControl<number | null>;
      schuldAanDeelneming: FormControl<number | null>;
      renteBetaaldAanDeelneming: FormControl<number | null>;
      situatiesDeelneming: FormGroup<{
        dochterIsGevoegdofOntvoegd: FormControl<boolean | null>;
        voegingsdatum: FormControl<Date | null>;
        ontvoegingsdatum: FormControl<Date | null>;
        belangIsVerworvenOfVergroot: FormControl<boolean | null>;
        percentageVerwerving: FormControl<number | null>;
        nominaleWaardeVerworvenBelang: FormControl<number | null>;
        opgeofferdBedragVerworvenBelang: FormControl<number | null>;
        belangIsVerworvenVanVerbondenPersoon: FormControl<boolean | null>;
        brutoVoordelenVerworvenDeelneming: FormControl<number | null>;
        kostenVerwervingDeelneming: FormControl<number | null>;
        belangIsVerkochtOfVerkleind: FormControl<boolean | null>;
        percentageVervreemding: FormControl<number | null>;
        nominaleWaardeVereemdeBelang: FormControl<number | null>;
        opbrengstVervreemdeBelang: FormControl<number | null>;
        belangIsVervreemdAanVerbondenPersoon: FormControl<boolean | null>;
        brutoVoordelenVervreemdeDeelneming: FormControl<number | null>;
        kostenVervreemdingDeelneming: FormControl<number | null>;
        deelnemingIsGeliquideerd: FormControl<boolean | null>;
        vereffeningsdatum: FormControl<Date | null>;
        liquidatieverlies: FormControl<number | null>;
        nietKwalificerendeBeleggingsdeelneming: FormControl<boolean | null>;
        waarderingsvoorschift13aVpbToegepast: FormControl<boolean | null>;
        wevDeelneming: FormControl<number | null>;
        wevDeelnemingOpMomentDalingOnder25: FormControl<number | null>;
        percentageIsGedaaldOnder25: FormControl<boolean | null>;
        bruteringVoordeel: FormControl<number | null>;
        deelnemingIsEUenVoordeelIsWinstuitkering: FormControl<boolean | null>;
        bruteringEnVerrekeningMetEffectieveBelasting: FormControl<boolean | null>;
        afwijkendeVerrekening: FormControl<boolean | null>;
        verkaptDividendOntvangen: FormControl<boolean | null>;
        bedragVerkaptVoordeel: FormControl<number | null>;
        omschrijvingVerkaptVoordeel: FormControl<string | null>;
        naamUiteindelijkeMoeder: FormControl<string | null>;
        adresUiteindelijkeMoeder: FormControl<Adres | null>;
        directeDeelnemingDeedVerkapteUitdeling: FormControl<boolean | null>;
        naamVennootschapDieVoordeelLietOntgaan: FormControl<string | null>;
        adresVennootschapDieVoordeelLietOntgaan: FormControl<Adres | null>;
      }>;
    }>;
  };

  @Input() public id!: string;
  private dataService = inject(BokkzVpbDataService);
  private destroyRef = inject(DestroyRef);

  public index = 0;
  public dialogVisible = false;

  public deelnemingenFg = new FormGroup({
    deelnemingen: new FormArray<typeof this.typeSafeFormShape.deelneming>([])
  });

  public optionsJaNee: SelectFormControlItem<boolean>[] = [
    {value: true, label: 'Ja'},
    {value: false, label: 'Nee'},
  ];

  readonly corporateTaxEntity$: Observable<CorporateTaxEntity> =
    this.dataService.corporateTaxEntity$.pipe(
      filter((data): data is CorporateTaxEntity => !!data?.sstRelationId),
      tap(data => {
        if (data.aangifte) {
          this.updateForm(data.aangifte);
        }
      }),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  showDialog(index: number): void {
    this.index = index;
    this.dialogVisible = true;
  }

  get deelnemingen(): FormArray<typeof this.typeSafeFormShape.deelneming> {
    return this.deelnemingenFg.controls.deelnemingen;
  }

  addRij(): void {
    this.deelnemingen.push(this.createFormGroup());
  }

  verwijderRij(index: number): void {
    this.deelnemingen.removeAt(index);
    this.saveForm();
  }

  createFormGroup(item?: Deelneming): typeof this.typeSafeFormShape.deelneming {
    return new FormGroup({
      naam: new FormControl<string | null>(item?.naam ?? null),
      fiscaalNummer: new FormControl<string | null>(item?.fiscaalNummer ?? null),
      percentageAandelenbezit: new FormControl<number | null>(item?.percentageAandelenbezit ?? null),
      nominaleWaardeAandelenbezit: new FormControl<number | null>(item?.nominaleWaardeAandelenbezit ?? null),
      opgeofferdBedrag: new FormControl<number | null>(item?.opgeofferdBedrag ?? null),
      balanswaardering: new FormControl<number | null>(item?.balanswaardering ?? null),
      voordelenBoekjaar: new FormControl<number | null>(item?.voordelenBoekjaar ?? null),
      vorderingOpDeelneming: new FormControl<number | null>(item?.vorderingOpDeelneming ?? null),
      renteOntvangenVanDeelneming: new FormControl<number | null>(item?.renteOntvangenVanDeelneming ?? null),
      schuldAanDeelneming: new FormControl<number | null>(item?.schuldAanDeelneming ?? null),
      renteBetaaldAanDeelneming: new FormControl<number | null>(item?.renteBetaaldAanDeelneming ?? null),
      situatiesDeelneming: new FormGroup({
        dochterIsGevoegdofOntvoegd: new FormControl<boolean | null>(item?.situatiesDeelneming?.dochterIsGevoegdofOntvoegd ?? false),
        voegingsdatum: new FormControl<Date | null>(item?.situatiesDeelneming?.voegingsdatum ?? null),
        ontvoegingsdatum: new FormControl<Date | null>(item?.situatiesDeelneming?.ontvoegingsdatum ?? null),
        belangIsVerworvenOfVergroot: new FormControl<boolean | null>(item?.situatiesDeelneming?.belangIsVerworvenOfVergroot ?? false),
        percentageVerwerving: new FormControl<number | null>(item?.situatiesDeelneming?.percentageVerwerving ?? null),
        nominaleWaardeVerworvenBelang: new FormControl<number | null>(item?.situatiesDeelneming?.nominaleWaardeVerworvenBelang ?? null),
        opgeofferdBedragVerworvenBelang: new FormControl<number | null>(item?.situatiesDeelneming?.opgeofferdBedragVerworvenBelang ?? null),
        belangIsVerworvenVanVerbondenPersoon: new FormControl<boolean | null>(item?.situatiesDeelneming?.belangIsVerworvenVanVerbondenPersoon ?? null),
        brutoVoordelenVerworvenDeelneming: new FormControl<number | null>(item?.situatiesDeelneming?.brutoVoordelenVerworvenDeelneming ?? null),
        kostenVerwervingDeelneming: new FormControl<number | null>(item?.situatiesDeelneming?.kostenVerwervingDeelneming ?? null),
        belangIsVerkochtOfVerkleind: new FormControl<boolean | null>(item?.situatiesDeelneming?.belangIsVerkochtOfVerkleind ?? false),
        percentageVervreemding: new FormControl<number | null>(item?.situatiesDeelneming?.percentageVervreemding ?? null),
        nominaleWaardeVereemdeBelang: new FormControl<number | null>(item?.situatiesDeelneming?.nominaleWaardeVereemdeBelang ?? null),
        opbrengstVervreemdeBelang: new FormControl<number | null>(item?.situatiesDeelneming?.opbrengstVervreemdeBelang ?? null),
        belangIsVervreemdAanVerbondenPersoon: new FormControl<boolean | null>(item?.situatiesDeelneming?.belangIsVervreemdAanVerbondenPersoon ?? null),
        brutoVoordelenVervreemdeDeelneming: new FormControl<number | null>(item?.situatiesDeelneming?.brutoVoordelenVervreemdeDeelneming ?? null),
        kostenVervreemdingDeelneming: new FormControl<number | null>(item?.situatiesDeelneming?.kostenVervreemdingDeelneming ?? null),
        deelnemingIsGeliquideerd: new FormControl<boolean | null>(item?.situatiesDeelneming?.deelnemingIsGeliquideerd ?? false),
        vereffeningsdatum: new FormControl<Date | null>(item?.situatiesDeelneming?.vereffeningsdatum ?? null),
        liquidatieverlies: new FormControl<number | null>(item?.situatiesDeelneming?.liquidatieverlies ?? null),
        nietKwalificerendeBeleggingsdeelneming: new FormControl<boolean | null>(item?.situatiesDeelneming?.nietKwalificerendeBeleggingsdeelneming ?? false),
        waarderingsvoorschift13aVpbToegepast: new FormControl<boolean | null>(item?.situatiesDeelneming?.waarderingsvoorschift13aVpbToegepast ?? false),
        wevDeelneming: new FormControl<number | null>(item?.situatiesDeelneming?.wevDeelneming ?? null),
        wevDeelnemingOpMomentDalingOnder25: new FormControl<number | null>(item?.situatiesDeelneming?.wevDeelnemingOpMomentDalingOnder25 ?? null),
        percentageIsGedaaldOnder25: new FormControl<boolean | null>(item?.situatiesDeelneming?.percentageIsGedaaldOnder25 ?? null),
        bruteringVoordeel: new FormControl<number | null>(item?.situatiesDeelneming?.bruteringVoordeel ?? null),
        deelnemingIsEUenVoordeelIsWinstuitkering: new FormControl<boolean | null>(item?.situatiesDeelneming?.deelnemingIsEUenVoordeelIsWinstuitkering ?? null),
        bruteringEnVerrekeningMetEffectieveBelasting: new FormControl<boolean | null>(item?.situatiesDeelneming?.bruteringEnVerrekeningMetEffectieveBelasting ?? null),
        afwijkendeVerrekening: new FormControl<boolean | null>(item?.situatiesDeelneming?.afwijkendeVerrekening ?? null),
        verkaptDividendOntvangen: new FormControl<boolean | null>(item?.situatiesDeelneming?.verkaptDividendOntvangen ?? false),
        bedragVerkaptVoordeel: new FormControl<number | null>(item?.situatiesDeelneming?.bedragVerkaptVoordeel ?? null),
        omschrijvingVerkaptVoordeel: new FormControl<string | null>(item?.situatiesDeelneming?.omschrijvingVerkaptVoordeel ?? null),
        naamUiteindelijkeMoeder: new FormControl<string | null>(item?.situatiesDeelneming?.naamUiteindelijkeMoeder ?? null),
        adresUiteindelijkeMoeder: new FormControl<Adres | null>(item?.situatiesDeelneming?.adresUiteindelijkeMoeder ?? null),
        directeDeelnemingDeedVerkapteUitdeling: new FormControl<boolean | null>(item?.situatiesDeelneming?.directeDeelnemingDeedVerkapteUitdeling ?? null),
        naamVennootschapDieVoordeelLietOntgaan: new FormControl<string | null>(item?.situatiesDeelneming?.naamVennootschapDieVoordeelLietOntgaan ?? null),
        adresVennootschapDieVoordeelLietOntgaan: new FormControl<Adres | null>(item?.situatiesDeelneming?.adresVennootschapDieVoordeelLietOntgaan ?? null),
      })
    });
  }

  trackByFn(index: number): number {
    return index;
  }

  ngOnInit(): void {
    this.corporateTaxEntity$.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe();

    this.deelnemingenFg.valueChanges.pipe(
      debounceTime(300),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      this.saveForm();
    });
  }

  private updateForm(vpbAangifte: VpbAangifte): void {
    const lijst = vpbAangifte.specificatieDeelnemingen || [];
    const formArray = this.deelnemingen;

    while (formArray.length > lijst.length) {
      formArray.removeAt(formArray.length - 1, {emitEvent: false});
    }

    for (let i = 0; i < lijst.length; i++) {
      if (i < formArray.length) {
        const control = formArray.at(i);
        control.patchValue(lijst[i], {emitEvent: false});
      } else {
        formArray.push(this.createFormGroup(lijst[i]), {emitEvent: false});
      }
    }
  }

  ngOnDestroy(): void {
    this.saveForm();
  }

  saveForm(): void {
    this.deelnemingenFg.updateValueAndValidity({emitEvent: false});
    const specificatieDeelnemingen = this.deelnemingenFg.getRawValue().deelnemingen as Deelneming[];

    this.dataService.patchCorporateTaxEntity(currentEntity => {
      if (!currentEntity.aangifte) {
        return currentEntity;
      }

      return {
        ...currentEntity,
        aangifte: {
          ...currentEntity.aangifte,
          specificatieDeelnemingen,
        }
      } as CorporateTaxEntity;
    });
  }
}
